// Shared speech-recognition language list + the localStorage key every
// mic-enabled control reads its default from. Previously each voice
// component (VoiceMicButton, VoiceFormFiller, ConsultationVoicePanel,
// VoiceIntake) carried its own copy of this list plus its own visible
// language picker — four redundant dropdowns a doctor could set
// independently and inconsistently, and none of it shared across a
// hospital's staff since localStorage is per browser.
//
// The language is now a single admin-configured, tenant-wide default
// (branding_settings.default_voice_language, edited in Admin Panel ->
// Branding -> Voice input) that useBranding.js syncs into this same
// localStorage key on every app load — every voice control below just
// reads it, with no per-field picker left to render. A doctor who
// genuinely needs a one-off different language for a single recording can
// still change it for the rest of the session via Admin Panel; this isn't
// wired for a live, no-reload override, since that wasn't how any of the
// four previous pickers worked either (each only took effect on next use).
export const VOICE_LANGUAGES = [
  { code: 'en-IN', short: 'EN', label: 'English' },
  { code: 'ta-IN', short: 'TA', label: 'தமிழ் (Tamil)' },
  { code: 'hi-IN', short: 'HI', label: 'हिंदी (Hindi)' },
  { code: 'te-IN', short: 'TE', label: 'తెలుగు (Telugu)' },
  { code: 'kn-IN', short: 'KN', label: 'ಕನ್ನಡ (Kannada)' },
  { code: 'ml-IN', short: 'ML', label: 'മലയാളം (Malayalam)' },
  { code: 'mr-IN', short: 'MR', label: 'मराठी (Marathi)' },
  { code: 'bn-IN', short: 'BN', label: 'বাংলা (Bengali)' },
  { code: 'gu-IN', short: 'GU', label: 'ગુજરાતી (Gujarati)' },
] as const;

export type VoiceLanguageCode = typeof VOICE_LANGUAGES[number]['code'];

export const VOICE_LANG_KEY = 'hms_voice_lang';

/** The current default speech-recognition language — synced from the tenant's admin setting on app load, falling back to English before that first sync ever runs. */
export function getVoiceLanguage(): string {
  return localStorage.getItem(VOICE_LANG_KEY) || 'en-IN';
}

export function voiceLanguageLabel(code: string): string {
  return VOICE_LANGUAGES.find(l => l.code === code)?.label ?? code;
}
